import {
    GetCustomUpgradesDocument,
    useArchiveCustomUpgradeMutation
} from "generated/graphql";
import { GraphQLError } from "graphql";
import { segmentTrackArchiveCustomUpgrade } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";

export type ArchiveCustomUpgradeResult =
    | { readonly ok: true }
    | { readonly ok: false; readonly message: string };

const parseApolloError = (errors: readonly GraphQLError[]): string => {
    const isPermissionDenied = errors.some(
        (gqlError) => gqlError.extensions?.code === "permission-error"
    );
    if (isPermissionDenied) {
        return "You don't have permission to archive upgrades for this team.";
    }

    return `Failed to archive upgrade: ${errors[0].message}`;
};

export interface UseArchiveCustomUpgradeResult {
    readonly archiveUpgrade: (
        id: string
    ) => Promise<ArchiveCustomUpgradeResult>;
    readonly submitting: boolean;
}

export const useArchiveCustomUpgrade = (): UseArchiveCustomUpgradeResult => {
    const cluster = useCluster();
    const [archiveCustomUpgrade, { loading }] =
        useArchiveCustomUpgradeMutation();

    const archiveUpgrade = async (
        id: string
    ): Promise<ArchiveCustomUpgradeResult> => {
        try {
            const { errors } = await archiveCustomUpgrade({
                variables: { id },
                refetchQueries: [GetCustomUpgradesDocument],
                errorPolicy: "all"
            });

            if (!errors) {
                segmentTrackArchiveCustomUpgrade({ cluster, upgradeId: id });
                return { ok: true };
            }

            return {
                ok: false,
                message: parseApolloError(errors)
            };
        } catch {
            return {
                ok: false,
                message: "Failed to archive upgrade"
            };
        }
    };

    return { archiveUpgrade, submitting: loading };
};
